import { Injectable, NgZone } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { GlobalServiceProvider } from "../../../providers/global-service/global-service";
import { GetCompanyData, RequestGetCompanyDto } from "../../../shared/nestjs/gd-only/dto/sf-company.dto";
import { AuthApi } from "../../../shared/nestjs/gd-only/services/auth.api";
import { SfCompanyApi } from "../../../shared/nestjs/gd-only/services/sf-company.api";
import { NestApiProvider } from "../../../shared/nestjs/nest-api";
import { SunfishApi, RegisterApi } from "../../../shared/sdk";
import { SFCompanyApi, ContactApi, PostServiceApi } from "../../../shared/sdk/services/custom";
import { SfProfileApi } from "../../../shared/nestjs/gd-only/services/sf-profile.api";
import { ContactNestApi } from "../../../shared/nestjs/gd-only/services/contact.api";
import { RequestGetEmpDto } from "../../../shared/nestjs/gd-only/dto/sf-profile.dto";
import { SFProfileApi } from './../../../shared/sdk/services/custom/SFProfile';
import { PostServiceNestApi } from "../../../shared/nestjs/gd-only/services/post-service.api";
import { RequestGetPostDto } from "../../../shared/nestjs/gd-only/dto/post-service.dto";
import { Platform } from "ionic-angular";
import { CameraNative } from "../../../native/camera.native";
import { CameraOptions } from "@ionic-native/camera";
import { FileUploaderProvider, OptionsUploadWithHttp } from "../../../providers/file-uploader/file-uploader";
import { AppPreferences } from "../../../shared/local/app-preferences.local";
import { StoragePathProvider } from "../../../providers/storage-path/storage-path";
import { PersonalSf6Provider } from "../../../shared/nestjs/gd-integrated/services/personal-sf6";
import { SkillSf6Api } from "../../../shared/nestjs/gd-integrated/services/skill.api";
import { ResponseSaveDto } from "../../../shared/nestjs/gd-integrated/dto/skill.dto";
import { NestResponsesProvider } from "../../../shared/nestjs/nest-responses";
import { GDAuthInterface } from "../../../model/gd-auth";

export interface ResponseProfileDto {
	accountName: string;
	companyCode: string;
	companyId: number;
	userId: number;
	username: string;
	empId: string;
	empNo: string;
	fullname: string;
	photo: string;
	position: string;
	positionId: number;
	email: string;
	phone: string;
	address: string;
	joinDate: Date;
	isSF6: number;
	birthdate: string;
	marital: number;
	gender: number;
}

export interface UploadProfileOptions {
	image: any,
	bodies: any,
	userId: any,
	eventsWebView?: any,
	photoName?: string // kalau di web required
}

@Injectable()
export class ProfileBloc {

	private subscriptions: Array<Subscription> = [];
	private _myProfile = {
		fullName: null,
		nickname: null,
		empId: null,
		empNo: null,
		photo: null,
		email: null,
		userId: null,
		username:null
	};

	constructor(
		public nestApiProvider: NestApiProvider,
		private authApi: AuthApi,
		private globalServiceProvider: GlobalServiceProvider,
		private sunfishApi: SunfishApi,
		private sfCompanyNestApi: SfCompanyApi,
		private SFCompanyApi: SFCompanyApi,
		private registerApi: RegisterApi,
		private sfprofileApi: SfProfileApi,
		private SFProfileApi: SFProfileApi,
		private contactNestApi: ContactNestApi,
		private contactApi: ContactApi,
		private postServiceApi: PostServiceApi,
		private postServiceNestApi: PostServiceNestApi,
		private platform: Platform,
		private cameraNative: CameraNative,
		private fileUploaderProvider: FileUploaderProvider,
		private appPreferences: AppPreferences,
		private storagePathProvider: StoragePathProvider,
		private personalSf6Provider: PersonalSf6Provider,
		private skillSf6Api: SkillSf6Api,
		public nestResponse: NestResponsesProvider,
		private ngZone: NgZone
	) { }

	/**
	 * Biodata
	 */
	public biodata(data: any): Observable<any> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.registerApi.onboarding_new(data)
				} else {
					return this.registerApi.onboarding_new(data)
				}
			})
	}

	/**
	 * User Data
	 */
	private apiGetUserData(): Observable<any> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.authApi.profile().pipe(map(response => response.data))
				} else {
					return this.sunfishApi.getUserData().pipe(map(async (response) => {
						const data = await this.globalServiceProvider.convertObjToCamelCase(response.result);
						return data
					}))
				}
			})
	}

	/**
	 * Company
	 */
	private apiGetCompany(payload: RequestGetCompanyDto): Observable<any> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.sfCompanyNestApi.getCompany(payload).pipe(map(async (response) => response.data))
				} else {
					return this.SFCompanyApi.getCompany(payload.companyId).pipe(map(async (response) => response[0]))
				}
			})
	}

	/**
	 *  Get Profile
	 */
	getEmp(payload: RequestGetEmpDto): Promise<any> {
		return new Promise((resolve, reject) => {
			this.subscriptions.push(
				this.apiGetEmp(payload)
					.subscribe(result => {
						resolve(result);
					}, error => {
						reject(error);
					})
			);
		});
	}

	/**
	 * Api Get Profile
	 */
	private apiGetEmp(payload: RequestGetEmpDto): Observable<any> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.sfprofileApi.getEmp(payload).pipe(map(async (response) => response.data[0]))
				} else {
					return this.sfprofileApi.getEmp(payload).pipe(map(async (response) => response.data[0]))
				}
			})
	}

	/**
	 *  Get Contact
	 */
	getContact(payload: { empId: string }): Promise<any> {
		return new Promise((resolve, reject) => {
			this.subscriptions.push(
				this.apiGetContact(payload)
					.subscribe(async (result) => {
						const response = await this.globalServiceProvider.convertObjToCamelCase(result);
						resolve(response);
					}, error => {
						reject(error);
					})
			);
		})
	}

	/**
	 * Api Get Contact
	 */
	private apiGetContact(payload: { empId: string }): Observable<any> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.contactNestApi.getContact(payload.empId).pipe(map((response) => response.data))
				} else {
					return this.contactApi.getContact(payload.empId).pipe(map((response) => response.result[0]))
				}
			})
	}

	/**
	 *  Get Post
	 */
	getPost(payload: RequestGetPostDto): Promise<Array<any>> {
		return new Promise((resolve, reject) => {
			payload.page = payload.skip + 1;
			this.subscriptions.push(
				this.apiGetPost(payload).subscribe((result) => {
					resolve(result);
				}, error => {
					reject(error);
				})
			)
		});
	}

	/**
	 * Api Get Post
	 */
	private apiGetPost(payload: RequestGetPostDto): Observable<Array<any>> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.postServiceNestApi.getPost(payload).pipe(map(async (response) => response.data))
				} else {
					return this.postServiceApi.getPost(payload);
				}
			})
	}

	dispose() {
		this.subscriptions.forEach((sub) => sub.unsubscribe);
	}

	getUserData() {
		return new Promise<ResponseProfileDto>((resolve, reject) => {
			this.subscriptions.push(
				this.apiGetUserData().subscribe((result) => {
					resolve(result)
				}, error => {
					reject(error);
				})
			)
		})
	}

	getCompany(payload: RequestGetCompanyDto) {
		return new Promise<GetCompanyData>((resolve, reject) => {
			this.subscriptions.push(
				this.apiGetCompany(payload).subscribe((result) => {
					resolve(result)
				}, error => {
					reject(error);
				})
			)
		})
	}

	saveFotoProfile(payload: { userId: any, namePicture: string, empId: string }) {
		return Observable.fromPromise(this.appPreferences.getAuth())
			.mergeMap(auth => {
				if (auth.isSF6) {
					return this.saveFotoProfileSF6(payload)
				} else {
					return this.saveFotoProfileGD(payload)
				}
			}).toPromise();
	}

	saveFotoProfileGD(payload: { userId: any, namePicture: string }) {
		return new Promise((resolve, reject) => {
			this.subscriptions.push(
				this.apiSaveFotoProfileGD(payload)
					.subscribe((result) => {
						resolve(result)
					}, error => {
						reject(error);
					})
			)
		})
	}

	/**
	 * Api save foto profile GD only
	 */
	private apiSaveFotoProfileGD(payload: { userId: string, namePicture: string }): Observable<Array<any>> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.sfprofileApi.saveFotoProfile(payload).pipe(map(async (response) => response.data))
				} else {
					return this.SFProfileApi.saveFotoProfile(payload)
				}
			})
	}

	saveFotoProfileSF6(payload: { empId: any, namePicture: string }) {
		return new Promise((resolve, reject) => {
			this.subscriptions.push(
				this.apiSaveFotoProfileSF6(payload)
					.subscribe((result) => {
						resolve(result)
					}, error => {
						reject(error);
					})
			)
		})
	}

	/**
	 * Api save foto profile
	 */
	private apiSaveFotoProfileSF6(payload: { empId: string, namePicture: string }): Observable<Array<any>> {
		return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
			.mergeMap(isGoPathNewExist => {
				if (isGoPathNewExist) {
					return this.personalSf6Provider.saveProfilePic(payload).pipe(map(async (response) => response.data))
				} else {
					return this.SFProfileApi.saveFotoProfile(payload)
				}
			})
	}

	async takePicture(isCamera: boolean) {
		if (this.platform.is('cordova')) {
			const cameraOption: CameraOptions = {
				sourceType: isCamera ? this.cameraNative.camera.PictureSourceType.CAMERA : this.cameraNative.camera.PictureSourceType.PHOTOLIBRARY
			}
			const camera = await this.cameraNative.getPicture(cameraOption)
			return camera.nativeURL;
		}
	}

	processWebImage(event) {
		return new Promise((resolve, reject) => {
			let name = event.target.files[0].name;
			let fileIMG = ['png', 'jpg', 'jpeg', 'bmp', 'gif'];
			let formatImage = name.split('.').pop();
			let res = formatImage.toLowerCase();

			if (fileIMG.indexOf(res) < 0) {
				reject('File type is invalid.')
			} else {
				const reader = new FileReader();
				reader.addEventListener('load', () => {
					resolve(reader.result);
				}, false);
				reader.readAsDataURL(event.target.files[0]);
			}
		})
	}

	uploadProfile(params: UploadProfileOptions) {
		return new Promise(async (resolve, reject) => {
			try {
				const path = await this.storagePathProvider.getStorageProfileCustom(params.userId);
				const SFGoAuth = await this.appPreferences.getAuth();
				const options: OptionsUploadWithHttp = {
					options: params.photoName || null,
					nativeURI: params.image,
					body: params.bodies,
					type: 'image',
					eventsWebview: params.eventsWebView || null
				}

				if (SFGoAuth.isSF6) {
					options.options = !this.platform.is('cordova') && params.photoName ? params.photoName + '.jpg' : null;
				}

				const customResponse = SFGoAuth.isSF6 ? false : true;
				let result = await this.fileUploaderProvider.uploadWithHttp(options, path.urlUpload, customResponse);
				console.log('result upload', result)
				if (SFGoAuth.isSF6) {
					result = result.data.SERVERFILE;
				}

				resolve(result);
			} catch (error) {
				reject(error)
			}
		})
	}

	createFileName(SFGoAuth, userId, isCamera: boolean, picture) {
		let result = isCamera ? 'IMG_' + this.formatDate() + '_' + userId + '_' + picture.substr(picture.lastIndexOf('/') + 1) : 'IMG_' + this.formatDate() + '_' + userId + '_' + this.makeid().toString();
		if (!SFGoAuth.isSF6) {
			return result + '.jpg';
		}
		return result;
	}

	formatDate() {
		let d = new Date(),
			month = '' + (d.getMonth() + 1),
			day = '' + d.getDate(),
			year = d.getFullYear();

		if (month.length < 2) month = '0' + month;
		if (day.length < 2) day = '0' + day;

		return [year, month, day].join('');
	}

	makeid() {
		let text = '';
		const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		for (let i = 0; i < 12; i++)
			text += possible.charAt(Math.floor(Math.random() * possible.length));
		return text;
	}

	/**
	 * Skill
	 */

	save(data): Observable<ResponseSaveDto> {
		return this.skillSf6Api.save(data);
	}

	listingSkill(payload): Observable<any> {
		return this.skillSf6Api.listing(
			payload.empId, payload.lang
		);
	}

	async getType(payload) {
		return new Promise<any>(async (resolve, reject) => {
			this.subscriptions.push(
				this.skillSf6Api.type(payload).subscribe(result => {
					let skillTypeList = [];
					if (result.data) {
						let objTemplate = {
							code: '0',
							name: 'Select',
							orderNo: 0
						}
						skillTypeList.push(objTemplate)

						for (const val of result.data) {
							let obj = {
								code: val.code,
								name: val.name,
								orderNo: val.orderNo
							}
							skillTypeList.push(obj)
						}
					}
					resolve(skillTypeList);
				}, error => {
					reject(error)
					console.log("error", error);
				})
			)
		})
	}

	getSocialMedia(empId: string) {
		return new Promise<Array<any>>((resolve, reject) => {
			this.subscriptions.push(
				this.sfprofileApi.getSocial(empId)
					.map(res => res.data)
					.subscribe(result => {
						resolve(result)
					}, error => {
						reject(error)
					})
			)
		})
	}

	async setMyProfile(auth?: GDAuthInterface) {
		auth = auth ? auth : await this.appPreferences.getAuth();
		console.log(auth, 'serah')
		this._myProfile.fullName = auth.fullName;
		this._myProfile.nickname = auth.nickname;
		this._myProfile.empId = auth.empId;
		this._myProfile.userId = auth.userId;
		this._myProfile.empNo = auth.empNo;
		this._myProfile.email = auth.email;
		this.myProfilePicture = auth.photo;
		this._myProfile.username = auth.userName;
	}

	async	changedProfilePicture(photoName: string) {
		const auth = await this.appPreferences.getAuth();
		const time = new Date().getTime().toString();

		if (auth.isSF6) {
			try {
				const paramsArray = auth.empphotopath.split('?');
				if (paramsArray.length <= 1) {
					this.myProfilePicture = `${photoName}?${time}`;
				} else {
					this.myProfilePicture = `${photoName}&${time}`;
				}
			} catch (error) {
				console.log(error);
				this.myProfilePicture = `${photoName}&${time}`;
			}
		} else {
			this.myProfilePicture = `${photoName}&${time}`;
		}
	}

	set myProfilePicture(url) {
		this.ngZone.run(() => {
			this._myProfile.photo = url;
		})
	}

	get myProfile() {
		return this._myProfile;
	}
}



// WEBPACK FOOTER //
// ./src/bloc/feature/profile/profile.bloc.ts